import flow from 'lodash/flow';
import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import withAuth from '../decorators/auth/withAuth';
import withBreakpoints from '../decorators/withBreakpoints';
import withCurrentRouteCustomProps from '../decorators/withCurrentRouteCustomProps';
import withDesktopApp from '../decorators/withDesktopApp';
import withMobileJourney from '../decorators/withMobileJourney';
import withContextProviders from '../decorators/withContextProviders';
import { fetchMe } from '../actions/me';
import { updateMWBypassCookie } from '../actions/app';
import { setCookie } from '../utils/cookie';
import connectAsync from '../utils/connectAsync';
import { selectUserAgent } from '../selectors/app';
import { mwLandingBypassCookie } from '../constants/cookies';
import { MW_LANDING_SHOW_AFTER_X_DAYS } from '../constants/experiments/mobileLanding';
import useActions from '../hooks/useActions';
import useExperiment from '../hooks/useExperiment';
import useClientAnalytics from '../hooks/useClientAnalytics';
import useOneTrust from '../hooks/useOneTrust';
import AppRootLoader from './AppRootLoader';
import MobileWebEntryLoader from './MobileWebEntryLoader';
import withGuideItemProfile from './withGuideItemProfile';
import { DiscordSplash, useDiscord } from './discord';
import '../styles/reset.css';
import '../styles/site.css';
import '../styles/fontFaceDeclarations.css';

const MOBILE_OVERLAY_EXPIRATION_DAYS = 1;

const AppConnector = memo((props) => {
  const {
    apiClient,
    onHistoryUpdate,
    location,
    match,
    route,
    routeProps,
    breakpoint,
    shouldShowMobileOverlay,
    history,
  } = props;
  const actions = useActions({ updateMWBypassCookie });
  const userAgent = useSelector(selectUserAgent);
  const mobileOverlayExpirationDaysOverride = useExperiment(
    MW_LANDING_SHOW_AFTER_X_DAYS,
  );
  const MobileWebLandingComponent = useMemo(
    () =>
      routeProps.guideContext.guideId
        ? withGuideItemProfile(MobileWebEntryLoader)
        : MobileWebEntryLoader,
    [routeProps.guideContext.guideId],
  );
  const { isDiscordReady, showDiscordSplashScreen, discordError } =
    useDiscord(history);
  const contentProps = {
    breakpoint,
    route,
    routeProps,
    location,
    apiClient,
    history,
    isDiscordReady,
  };

  useOneTrust({ isMobileBlockerFlow: shouldShowMobileOverlay });
  useClientAnalytics(routeProps, match, onHistoryUpdate);

  if (showDiscordSplashScreen) {
    return (
      <DiscordSplash
        isDiscordReady={isDiscordReady}
        discordError={discordError}
        history={history}
      />
    );
  }

  if (shouldShowMobileOverlay) {
    // TODO: this should be moved to MobileWebEntry
    const handleContinueClick = () => {
      const expDays =
        mobileOverlayExpirationDaysOverride || MOBILE_OVERLAY_EXPIRATION_DAYS;

      setCookie(mwLandingBypassCookie.name, expDays);
      actions.updateMWBypassCookie(true);
    };

    return (
      <MobileWebLandingComponent
        routeProps={routeProps}
        match={match}
        isLanding={routeProps.isLanding}
        location={location}
        userAgent={userAgent}
        onContinueClick={handleContinueClick}
      />
    );
  }

  return <AppRootLoader {...contentProps} />;
});

AppConnector.propTypes = {
  apiClient: PropTypes.object,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  route: PropTypes.object.isRequired,
  routeProps: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  store: PropTypes.object,
  onHistoryUpdate: PropTypes.func,
  // withBreakpoints
  breakpoint: PropTypes.number.isRequired,
  // withMobileJourney
  shouldShowMobileOverlay: PropTypes.bool,
};

export default flow(
  withMobileJourney,
  withBreakpoints, // IMPORTANT: This needs to render after withCurrentRouteCustomProps
  withCurrentRouteCustomProps,
  withAuth,
  connectAsync([fetchMe]),
  withDesktopApp,
  withContextProviders,
)(AppConnector);
